type RmdfRawMaterialSource = {
  tcProductCategoryCode?: string;
  tcProductDetailCode?: string;
  rawMaterialCode?: string;
  rawMaterialPercentage?: number;
  standard?: string;
  isCertified?: string;
  tcProductNetShippingWeight?: number;
  tcProductSupplementaryWeight?: number;
  tcProductAdditivesWeight?: number;
};

export const INCOMING_TC_STEP_LABELS = [
  "Incoming TC Information",
  "Incoming TC Product Information",
  "Incoming TC Net Weight Used Information",
] as const;

export const getIncomingTcSourceVersion = (
  incomingV4IssuedFlag: unknown
): "V3" | "V4" =>
  String(incomingV4IssuedFlag || "").trim().toUpperCase() === "Y" ? "V4" : "V3";

const extractLeadingCode = (value: any): string => {
  const text = String(value || "").trim();
  if (!text) return "";
  const directMatch = text.match(/\b(?:PC|PD|RM)\d{4}\b/i);
  return directMatch ? directMatch[0].toUpperCase() : text.split(" - ")[0].trim();
};

const extractWeight = (value: any): number => {
  const weight = Number(value);
  return Number.isFinite(weight) ? Math.abs(weight) : 0;
};

export const filterIncomingCertificateTypes = <T extends { value: string; label?: string }>(
  options: T[],
  incomingV4IssuedFlag: unknown
): T[] => {
  const isV4 = String(incomingV4IssuedFlag || "").trim().toUpperCase() === "Y";
  if (isV4) {
    return options
      .filter((option) => String(option.value || "").trim().toUpperCase() === "TC")
      .map((option) => ({ ...option, label: "TC V4" }));
  }
  return options.map((option) => {
    const val = String(option.value || "").trim().toUpperCase();
    if (val === "TC" || val === "V3") {
      return { ...option, label: "TC V3.1" };
    }
    return option;
  });
};

export const ensureExternalIncomingCertificateTypes = <
  T extends { value: string; label: string }
>(options: T[]): Array<T | { value: string; label: string }> => {
  const normalizedOptions = options.map((option) => {
    const val = String(option.value || "").trim().toUpperCase();
    if (val === "FPF") {
      return { ...option, label: "FPF" };
    }
    if (val === "TC" || val === "V3") {
      return { ...option, label: "TC" };
    }
    return option;
  });
  const availableValues = new Set(
    normalizedOptions.map((option) =>
      String(option.value || "").trim().toUpperCase()
    )
  );
  const fallbackOptions = [
    { value: "RMDF", label: "RMDF" },
    { value: "FPF", label: "FPF" },
  ].filter((option) => !availableValues.has(option.value));

  return [...normalizedOptions, ...fallbackOptions];
};

export const unwrapRmdfLookupItems = (payload: any): any[] => {
  let items = payload?.response ?? payload ?? [];
  if (typeof items === "string") {
    try {
      items = JSON.parse(items);
    } catch {
      return [];
    }
  }
  return Array.isArray(items) ? items : [];
};

export const extractRmdfPercentage = (item: any, rawMaterialCode?: string): number => {
  const rm = item?.reclaimedMaterials ?? item;
  const direct = Number(
    rm?.rawMaterialPercentage ??
    rm?.rmPercentage ??
    rm?.certifiedMaterialComposition ??
    rm?.certifiedMaterialPercentage ??
    rm?.reclaimedMaterial1Percentage ??
    item?.reclaimedMaterial1Percentage
  );
  if (Number.isFinite(direct) && direct > 0) return direct;

  const composition = String(rm?.rmComposition || item?.rmComposition || "");
  const normalizedCode = extractLeadingCode(rawMaterialCode).toUpperCase();
  if (normalizedCode) {
    const escapedCode = normalizedCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const codeMatch = composition.match(
      new RegExp(`\\b${escapedCode}\\s*[-:]\\s*(\\d+(?:\\.\\d+)?)\\b`, "i")
    );
    const codePercentage = Number(codeMatch?.[1]);
    if (Number.isFinite(codePercentage) && codePercentage > 0) return codePercentage;
  }

  // Do not apply the final suffix of a multi-RM composition to every row.
  const compositionMatches = Array.from(
    composition.matchAll(/\bRM\d{4}\s*[-:]\s*(\d+(?:\.\d+)?)\b/gi)
  );
  if (compositionMatches.length === 1) {
    const percentage = Number(compositionMatches[0][1]);
    if (Number.isFinite(percentage) && percentage > 0) return percentage;
  }
  return 100;
};

export const normalizeRmdfCertifiedFlag = (item: any): "Y" | "N" => {
  const explicitFlag = String(item?.isCertified ?? "").trim().toUpperCase();
  if (["Y", "YES", "TRUE"].includes(explicitFlag)) return "Y";
  if (["N", "NO", "FALSE"].includes(explicitFlag)) return "N";
  if (typeof item?.isSellerScopeCode === "boolean") {
    return item.isSellerScopeCode ? "Y" : "N";
  }
  return "Y";
};

export const buildRmdfArticleMaps = (rawMaterials: any[]) => {
  const standardsByArticle: { [productNo: string]: string[] } = {};
  const rawMaterialsByArticle: { [productNo: string]: RmdfRawMaterialSource[] } = {};
  const maxWeightByArticle: { [productNo: string]: number } = {};
  const sourceByArticle: { [productNo: string]: RmdfRawMaterialSource } = {};

  const unwrapRmItems = (items: any[]): any[] => {
    return (items || []).flatMap((entry: any) => {
      if (entry?.reclaimedMaterials) {
        return Array.isArray(entry.reclaimedMaterials)
          ? entry.reclaimedMaterials.map((rm: any) => ({ ...entry, reclaimedMaterials: rm }))
          : [entry];
      }
      return [entry];
    });
  };

  unwrapRmItems(rawMaterials).forEach((item: any) => {
    const rm = item?.reclaimedMaterials ?? item;
    const articleNo = String(rm?.productNumber || item?.productNumber || rm?.productNo || item?.productNo || "").trim();
    if (!articleNo) return;

    standardsByArticle[articleNo] = ["RMDF"];
    const availableWeight = extractWeight(
      rm?.balance || rm?.certifiedWeightBought || rm?.totalReclaimedWeight || item?.totalReclaimedWeight
    );
    const rawMaterialCode = extractLeadingCode(
      rm?.rmCode || rm?.certifiedRmCode || rm?.rawMaterialCode || rm?.reclaimedMaterial1 || item?.reclaimedMaterial1
    );
    const sourceRow: RmdfRawMaterialSource = {
      tcProductCategoryCode: extractLeadingCode(rm?.productCategory || rm?.preOrPostConsumerMaterial || item?.preOrPostConsumerMaterial),
      tcProductDetailCode: extractLeadingCode(rm?.productDetail || rm?.feedStock || item?.feedStock),
      rawMaterialCode,
      rawMaterialPercentage: extractRmdfPercentage(rm, rawMaterialCode),
      standard: "RMDF",
      isCertified: normalizeRmdfCertifiedFlag(rm),
      tcProductNetShippingWeight: availableWeight,
      tcProductSupplementaryWeight: 0,
      tcProductAdditivesWeight: 0,
    };
    rawMaterialsByArticle[articleNo] = rawMaterialsByArticle[articleNo] || [];
    const duplicate = rawMaterialsByArticle[articleNo].some(
      (existing) =>
        existing.tcProductCategoryCode === sourceRow.tcProductCategoryCode &&
        existing.tcProductDetailCode === sourceRow.tcProductDetailCode &&
        existing.rawMaterialCode === sourceRow.rawMaterialCode &&
        existing.rawMaterialPercentage === sourceRow.rawMaterialPercentage
    );
    if (!duplicate) rawMaterialsByArticle[articleNo].push(sourceRow);
    sourceByArticle[articleNo] = sourceByArticle[articleNo] || sourceRow;

    if (availableWeight > 0) {
      maxWeightByArticle[articleNo] = (maxWeightByArticle[articleNo] || 0) + availableWeight;
    }
  });

  return {
    standards: ["RMDF"],
    standardsByArticle,
    sourceByArticle,
    rawMaterialsByArticle,
    maxWeightByArticle,
  };
};
